import React, { useState, useEffect, useMemo } from 'react';
import { useAppSelector } from '../../hooks/useAppSelector';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';
import Toast from '../Toast';
import LoadingOverlay from '../LoadingOverlay';

const AttendanceManagement = () => {
    const { user, restaurant } = useAppSelector(state => state.auth);
    const restaurantId = restaurant?._id || user?.id;
    
    const [attendance, setAttendance] = useState([]);
    const [currentAttendance, setCurrentAttendance] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    useEffect(() => {
        if (restaurantId) {
            fetchCurrentAttendance();
            fetchAttendance();
        }
    }, [restaurantId, selectedDate]);

    const fetchAttendance = async () => {
        try {
            setLoading(true);
            const response = await axios.get(API_ENDPOINTS.ATTENDANCE_GET, {
                params: { restaurantId, date: selectedDate }
            });
            setAttendance(response.data);
        } catch (error) {
            showToast('Failed to fetch attendance', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchCurrentAttendance = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.ATTENDANCE_CURRENT(restaurantId));
            setCurrentAttendance(response.data);
        } catch (error) {
            console.error('Failed to fetch current attendance:', error);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const handleCheckIn = async (staffId, shift) => {
        setActionLoading(true);
        setLoadingMessage('Processing check-in...');
        try {
            await axios.post(API_ENDPOINTS.ATTENDANCE_CHECKIN, {
                staffId,
                shift,
                restaurantId
            });
            showToast('Checked in successfully');
            await fetchCurrentAttendance();
            await fetchAttendance();
        } catch (error) {
            showToast(error.response?.data?.message || 'Check-in failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCheckOut = async (staffId, shift) => {
        setActionLoading(true);
        setLoadingMessage('Processing check-out...');
        try {
            await axios.post(API_ENDPOINTS.ATTENDANCE_CHECKOUT, {
                staffId,
                shift
            });
            showToast('Checked out successfully');
            await fetchCurrentAttendance();
            await fetchAttendance();
        } catch (error) {
            showToast(error.response?.data?.message || 'Check-out failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    // Calculate total working hours by staff member
    const staffWorkingHours = useMemo(() => {
        const hoursMap = {};
        
        attendance.forEach((record) => {
            if (record.staffId && record.totalHours) {
                const staffId = record.staffId._id || record.staffId;
                const staffName = record.staffId?.name || 'Unknown';
                
                if (!hoursMap[staffId]) {
                    hoursMap[staffId] = {
                        staffId,
                        staffName,
                        totalHours: 0,
                        recordCount: 0
                    };
                }
                
                hoursMap[staffId].totalHours += record.totalHours || 0;
                hoursMap[staffId].recordCount += 1;
            }
        });
        
        // Convert to array and sort by total hours (descending)
        return Object.values(hoursMap)
            .sort((a, b) => b.totalHours - a.totalHours)
            .map(item => ({
                ...item,
                totalHours: Math.round(item.totalHours * 100) / 100 // Round to 2 decimal places
            }));
    }, [attendance]);

    return (
        <div className="relative overflow-x-hidden min-w-0">
            {actionLoading && <LoadingOverlay message={loadingMessage} />}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                <h2 className="text-base font-bold text-stone-800 truncate">Attendance</h2>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={async () => {
                            await fetchCurrentAttendance();
                            await fetchAttendance();
                            showToast('Attendance data refreshed successfully');
                        }}
                        disabled={loading}
                        className="inline-flex items-center justify-center p-2 rounded-lg font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-60 transition-colors shadow-sm size-9"
                        title="Refresh"
                        aria-label="Refresh"
                    >
                        {loading ? (
                            <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <span className="text-sm">↻</span>
                        )}
                    </button>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="px-2.5 py-2 text-xs border border-stone-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none bg-white"
                    />
                </div>
            </div>

            <div className="mb-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1.5">Currently checked in</p>
                <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-3">
                    {currentAttendance.length === 0 ? (
                        <p className="text-stone-500 text-xs">No one checked in</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            {currentAttendance.map((record) => (
                                <div key={record._id} className="p-2.5 rounded-lg bg-orange-50/80 border border-orange-100">
                                    <p className="font-semibold text-stone-800 text-xs">{record.staffId?.name}</p>
                                    <p className="text-[10px] text-stone-600">{record.shift}</p>
                                    <p className="text-[10px] text-stone-500">In: {new Date(record.checkInTime).toLocaleTimeString()}</p>
                                    <button
                                        type="button"
                                        onClick={() => handleCheckOut(record.staffId._id, record.shift)}
                                        className="mt-1.5 px-2 py-1 text-[10px] font-semibold rounded-lg bg-red-500 text-white hover:bg-red-600"
                                    >
                                        Check Out
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="mb-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1.5">Working hours by staff</p>
                <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-3">
                    {staffWorkingHours.length === 0 ? (
                        <p className="text-stone-500 text-xs">No hours for selected date</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {staffWorkingHours.map((staff) => (
                                <div key={staff.staffId} className="p-2.5 rounded-lg bg-stone-50 border border-stone-200">
                                    <div className="flex justify-between items-start mb-1">
                                        <h4 className="font-semibold text-stone-800 text-xs">{staff.staffName}</h4>
                                        <span className="text-[10px] bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded">{staff.recordCount} shift{staff.recordCount !== 1 ? 's' : ''}</span>
                                    </div>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-lg font-bold text-orange-600">{staff.totalHours}</span>
                                        <span className="text-[10px] text-stone-500">hrs</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1.5">Records</p>
                {loading ? (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-400 border-t-transparent" />
                    </div>
                ) : (
                    <>
                        <div className="hidden md:block bg-white rounded-xl border border-stone-200 shadow-sm overflow-x-auto">
                            <table className="w-full min-w-[560px] text-xs">
                                <thead className="bg-stone-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-semibold text-stone-600">Staff</th>
                                        <th className="px-3 py-2 text-left font-semibold text-stone-600">Shift</th>
                                        <th className="px-3 py-2 text-left font-semibold text-stone-600">Check In</th>
                                        <th className="px-3 py-2 text-left font-semibold text-stone-600">Check Out</th>
                                        <th className="px-3 py-2 text-left font-semibold text-stone-600">Hours</th>
                                        <th className="px-3 py-2 text-left font-semibold text-stone-600">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {attendance.map((record) => (
                                        <tr key={record._id} className="border-t border-stone-100 hover:bg-stone-50/50">
                                            <td className="px-3 py-2 font-medium text-stone-800">{record.staffId?.name}</td>
                                            <td className="px-3 py-2 text-stone-600">{record.shift}</td>
                                            <td className="px-3 py-2 text-stone-600">{record.checkInTime ? new Date(record.checkInTime).toLocaleString() : '—'}</td>
                                            <td className="px-3 py-2 text-stone-600">{record.checkOutTime ? new Date(record.checkOutTime).toLocaleString() : '—'}</td>
                                            <td className="px-3 py-2">{record.totalHours || 0} hrs</td>
                                            <td className="px-3 py-2">
                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                                    record.status === 'PRESENT' ? 'bg-emerald-100 text-emerald-800' :
                                                    record.status === 'LATE' ? 'bg-amber-100 text-amber-800' :
                                                    'bg-red-100 text-red-800'
                                                }`}>{record.status}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="md:hidden space-y-2">
                            {attendance.length === 0 ? (
                                <div className="bg-white rounded-xl border border-stone-200 p-3 text-center text-stone-500 text-xs">No records for this date</div>
                            ) : (
                                attendance.map((record) => (
                                    <div key={record._id} className="bg-white rounded-xl border border-stone-200 p-3 border-l-4 border-l-orange-400">
                                        <div className="flex justify-between items-start mb-1">
                                            <p className="font-semibold text-stone-800 text-xs">{record.staffId?.name}</p>
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                                record.status === 'PRESENT' ? 'bg-emerald-100 text-emerald-800' :
                                                record.status === 'LATE' ? 'bg-amber-100 text-amber-800' :
                                                'bg-red-100 text-red-800'
                                            }`}>{record.status}</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10px] text-stone-600">
                                            <span>Shift</span><span className="font-medium">{record.shift}</span>
                                            <span>In</span><span className="font-medium">{record.checkInTime ? new Date(record.checkInTime).toLocaleString() : '—'}</span>
                                            <span>Out</span><span className="font-medium">{record.checkOutTime ? new Date(record.checkOutTime).toLocaleString() : '—'}</span>
                                            <span>Hours</span><span className="font-medium">{record.totalHours || 0} hrs</span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                )}
            </div>

            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </div>
    );
};

export default AttendanceManagement;

